/**
 * Event images — HQ's poster and photo gallery, for the phone and desktop builds.
 *
 * HQ attaches them in the admin portal; they are public URLs of any shape (the
 * poster is usually a portrait A4, photos are usually landscape). Nothing here
 * fetches the bytes — every image is a plain <img src> — so an image that fails
 * to load is simply dropped: from the card, the page and the viewer alike, never
 * left as a broken-image icon or an empty frame.
 *
 * The gallery may also hold videos (`video: true`, Kayden 2026-10-26). A video
 * tile is a <video preload="metadata"> showing its first frame under a play
 * badge: the browser reads the header and one frame, not the file. A video the
 * browser cannot play is dropped like a broken image. Videos are never in the
 * service worker's precache (they are remote URLs, not build assets).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { eventImages } from '../../lib/events';

/**
 * The event's images minus any that failed to load, with the poster split out.
 * `items` is what the viewer pages through: the poster (when there is one) is
 * index 0, and photo i sits at `photoIndex(i)`.
 */
export function useEventImages(ev) {
  const [broken, setBroken] = useState(() => new Set());
  const items = eventImages(ev).filter((im) => !broken.has(im.url));
  const poster = items[0]?.kind === 'poster' ? items[0] : null;
  const photos = items.filter((im) => im.kind === 'photo');
  const markBroken = useCallback((url) => {
    setBroken((s) => (s.has(url) ? s : new Set(s).add(url)));
  }, []);
  return { items, poster, photos, markBroken, photoIndex: (i) => (poster ? i + 1 : i) };
}

const activate = (fn) => (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(); } };

/**
 * The poster in its own shape. HQ's poster may be portrait, square or a wide
 * banner, so nothing here assumes one: the image is as wide as its slot and as
 * tall as its own proportions make it, up to `maxHeight`. A poster taller than
 * that is shown whole (never cropped) over a blurred, dimmed copy of itself,
 * so the slot is filled with its own colours instead of grey bars.
 *
 * Until the image arrives the slot holds `placeholder` px, so the page does
 * not jump twice. A poster that fails to load removes the whole block
 * (`onBroken`, or on its own when no handler is given).
 */
export function PosterCover({ url, alt, maxHeight, placeholder = 160, onOpen, onBroken, radius = 0, expandHint = false, lazy = false, style }) {
  const [loaded, setLoaded] = useState(false);
  const [broken, setBroken] = useState(false);
  if (!url || broken) return null;
  const fail = () => { if (onBroken) onBroken(url); else setBroken(true); };
  const press = onOpen ? {
    role: 'button',
    tabIndex: 0,
    'aria-label': 'View the poster full size',
    onClick: (e) => { e.stopPropagation(); onOpen(); },
    onKeyDown: activate(onOpen),
  } : {};
  return (
    <div
      {...press}
      style={{
        position: 'relative', overflow: 'hidden', borderRadius: radius, background: 'var(--surface2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: loaded ? 0 : placeholder, cursor: onOpen ? 'zoom-in' : undefined, ...style,
      }}
    >
      {loaded && (
        <>
          <img
            src={url}
            alt=""
            aria-hidden="true"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(24px) saturate(1.15)', transform: 'scale(1.2)' }}
          />
          <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'rgba(10,16,28,.28)' }} />
        </>
      )}
      <img
        src={url}
        alt={alt}
        loading={lazy ? 'lazy' : undefined}
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={fail}
        style={{ position: 'relative', display: 'block', width: '100%', height: 'auto', maxHeight, objectFit: 'contain', opacity: loaded ? 1 : 0, transition: 'opacity .2s' }}
      />
      {expandHint && loaded && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute', right: 10, bottom: 10, width: 30, height: 30, borderRadius: 999,
            background: 'rgba(10,16,28,.55)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </span>
      )}
    </div>
  );
}

/**
 * The poster as a small square for compact list rows: the whole poster,
 * whatever its shape, inside the square over its own blurred colours.
 */
export function PosterSquare({ url, alt, size = 64, style }) {
  const [broken, setBroken] = useState(false);
  if (!url || broken) return null;
  return (
    <div style={{ position: 'relative', overflow: 'hidden', width: size, height: size, flex: 'none', background: 'var(--surface2)', ...style }}>
      <img src={url} alt="" aria-hidden="true" loading="lazy" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(10px)', transform: 'scale(1.2)' }} />
      <img
        src={url}
        alt={alt}
        loading="lazy"
        decoding="async"
        onError={() => setBroken(true)}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }}
      />
    </div>
  );
}

/** The round play badge over a video tile. */
export function PlayBadge({ size = 34 }) {
  return (
    <span
      aria-hidden="true"
      style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: size, height: size,
        borderRadius: 999, background: 'rgba(10,16,28,.6)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 1px 4px rgba(0,0,0,.3)', pointerEvents: 'none',
      }}
    >
      <svg width={size * 0.45} height={size * 0.45} viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: size * 0.06 }}>
        <path d="M7 4.5v15a1 1 0 0 0 1.5.86l12.5-7.5a1 1 0 0 0 0-1.72L8.5 3.64A1 1 0 0 0 7 4.5z" />
      </svg>
    </span>
  );
}

/**
 * HQ's photos (and videos) as square tiles. The grid fills the width it is
 * given with tiles no narrower than `min`, so the same component gives three
 * across on a phone and five or six on a desk. Tap one to open the viewer on it.
 */
export function PhotoGrid({ photos, title, min = 96, onOpen, onBroken }) {
  if (!photos.length) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: 8 }}>
      {photos.map((p, i) => (
        <div
          key={p.url}
          role="button"
          tabIndex={0}
          aria-label={`${p.video ? 'Play video' : 'View photo'} ${i + 1} of ${photos.length}`}
          onClick={() => onOpen(i)}
          onKeyDown={activate(() => onOpen(i))}
          className="tap"
          style={{ position: 'relative', aspectRatio: '1 / 1', borderRadius: 10, overflow: 'hidden', background: p.video ? '#1F2937' : 'var(--surface2)', cursor: p.video ? 'pointer' : 'zoom-in' }}
        >
          {p.video ? (
            <>
              {/* #t=0.1: iOS paints no frame for an unplayed video at 0. */}
              <video
                src={`${p.url}#t=0.1`}
                preload="metadata"
                muted
                playsInline
                disablePictureInPicture
                tabIndex={-1}
                onError={() => onBroken(p.url)}
                style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
              />
              <PlayBadge />
            </>
          ) : (
            <img
              src={p.url}
              alt={`${title} — photo ${i + 1}`}
              loading="lazy"
              decoding="async"
              onError={() => onBroken(p.url)}
              style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
            />
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * A gallery video in a viewer. Plays only on request (controls), pauses as soon
 * as it is not the item on screen, and starts by itself when `autoPlay` (the
 * tile that was tapped). Clicks stay on the video so they never close the viewer.
 */
export function GalleryVideo({ url, active = true, autoPlay = false, onReady, onError, style }) {
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!active) { el.pause(); return; }
    // Once: swiping back to the tapped video does not restart it. A browser
    // may refuse to start with sound; the controls are there either way.
    if (autoPlay && !started.current) {
      started.current = true;
      el.play()?.catch?.(() => {});
    }
  }, [active, autoPlay]);
  return (
    <video
      ref={ref}
      src={`${url}#t=0.1`}
      controls
      playsInline
      preload="metadata"
      onClick={(e) => e.stopPropagation()}
      onLoadedMetadata={onReady}
      onError={onError}
      style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 12, background: '#000', ...style }}
    />
  );
}
